import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { I18N_KEYS } from '@/language/keys';
import { NodeType } from '@/enums/node-type.enum';
import { FF_PACK_UNPACK_NODE } from '@/consts/featureFlags';
import { useNodeActions } from '@/components/Nodes/useNodeActions';
import { usePackAnalysis } from '@/components/Nodes/usePackNodes';
import { useWorkflowStore } from '@/state/workflow.state';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { useSaveRecipe } from '@/hooks/useSaveRecipe';
import { useGroupsManagerView, useGetNodes, useNodeAssetManager } from '../FlowContext';
import { ClearAssetsSubmenu } from './ClearAssetsSubmenu';
import type { MenuAction } from '@/components/Menu/Actions';
import type { NodeId } from '@/designer/designer';

interface MultiSelectionContextMenuProps {
  positionX: number;
  positionY: number;
  isOpen: boolean;
  onClose: () => void;
  selectedNodeIds: NodeId[];
}

export const MultiSelectionContextMenu = ({
  positionX,
  positionY,
  isOpen,
  onClose,
  selectedNodeIds,
}: MultiSelectionContextMenuProps) => {
  const { t } = useTranslation();
  const groupsManagerView = useGroupsManagerView();
  const getNodes = useGetNodes();
  const { duplicateNodes } = useNodeActions();
  const { deleteNodes } = useNodeActions();
  const nodeAssetManager = useNodeAssetManager();
  const { id: recipeId } = useWorkflowStore((state) => state.recipe);
  const saveRecipe = useSaveRecipe();
  const { downloadFromMultipleNodesWithProgress } = useInteractiveDownloader();
  const isPackUnpackEnabled = useFeatureFlagEnabled(FF_PACK_UNPACK_NODE);
  const { canPack, labelKey: packLabelKey, handlePack } = usePackAnalysis(selectedNodeIds);

  const selectedNodes = useMemo(() => {
    const allNodes = getNodes();
    const selectedNodeIdsSet = new Set(selectedNodeIds);
    return allNodes.filter((node) => selectedNodeIdsSet.has(node.id));
  }, [getNodes, selectedNodeIds]);

  const hasGroupNode = useMemo(() => selectedNodes.some((node) => node.type === NodeType.CustomGroup), [selectedNodes]);
  const recipeName = useWorkflowStore((state) => state.recipe.name);

  const hasDownloadable = useMemo(
    () => nodeAssetManager.hasDownloadableNodes(selectedNodes),
    [nodeAssetManager, selectedNodes],
  );

  const handleDeleteAll = useCallback(() => {
    deleteNodes(selectedNodeIds);
    onClose();
  }, [deleteNodes, selectedNodeIds, onClose]);

  const handleDuplicate = useCallback(() => {
    duplicateNodes(selectedNodeIds);
    onClose();
  }, [duplicateNodes, selectedNodeIds, onClose]);

  const handleGroupSelection = useCallback(() => {
    groupsManagerView.createGroupFromSelectedNodes();
    onClose();
  }, [groupsManagerView, onClose]);

  const handleCreateIterator = useCallback(() => {
    handlePack();
    onClose();
  }, [handlePack, onClose]);

  const handleDownloadAll = useCallback(async () => {
    const nodesWithAssets = await nodeAssetManager.collectAssetsFromNodes(selectedNodes, recipeId, saveRecipe);
    await downloadFromMultipleNodesWithProgress(nodesWithAssets, recipeName);
    onClose();
  }, [
    downloadFromMultipleNodesWithProgress,
    nodeAssetManager,
    selectedNodes,
    recipeId,
    saveRecipe,
    recipeName,
    onClose,
  ]);

  const menu: MenuAction[] = useMemo(
    () => [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.DOWNLOAD_ALL),
        action: () => {
          // Ensures the action returns void to comply with MenuAction interface
          void handleDownloadAll();
        },
        disabled: !hasDownloadable,
      },
      {
        type: 'slot',
        el: (
          <ClearAssetsSubmenu
            nodes={selectedNodes}
            onClose={onClose}
            clearAllLabel={t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.CLEAR_ALL_ASSETS)}
          />
        ),
      },
      { type: 'divider' },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.GROUP_SELECTION),
        action: handleGroupSelection,
        disabled: hasGroupNode,
        shortcutKey: 'G',
      },
      ...(isPackUnpackEnabled && canPack
        ? [
            { type: 'divider' } as const,
            {
              name: t(packLabelKey),
              action: handleCreateIterator,
            },
            { type: 'divider' } as const,
          ]
        : []),
      {
        name: t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.DUPLICATE),
        action: handleDuplicate,
      },
      { type: 'divider' },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.DELETE_ALL),
        action: handleDeleteAll,
      },
    ],

    [
      t,
      handleDeleteAll,
      handleDuplicate,
      handleDownloadAll,
      handleGroupSelection,
      handleCreateIterator,
      hasDownloadable,
      hasGroupNode,
      isPackUnpackEnabled,
      canPack,
      packLabelKey,
      onClose,
      selectedNodes,
    ],
  );

  return (
    <AppContextMenu open={isOpen} onClose={onClose} mouseX={positionX} mouseY={positionY} items={menu} width="180px" />
  );
};
